/**
 * TOOLS MANIFEST — the single source of truth for feature discoverability.
 *
 * Redesign Phase 0 (docs/plans/2026-06-25-frontend-redesign-plan.md §0).
 *
 * Every routed feature is ONE entry here. The manifest powers four surfaces:
 *   1. role-scoped left-nav GROUPS        3. the ⌘K command palette
 *   2. the Tools Hub grids                4. breadcrumbs
 *
 * `routeCoverage.test.ts` asserts that EVERY `<Route path="…">` in `App.tsx`
 * and `router/externalAccountantRoutes.tsx` appears either in TOOLS_MANIFEST
 * (as `route`) or in EXCLUDED_ROUTES. **An unregistered route fails the build.**
 * This is the anti-orphan guarantee: a feature with a route cannot hide.
 *
 * §0.1 — the manifest is a *capability* registry, not just a route registry.
 * Non-routed backend capabilities (the AI co-pilot, the Engine Audit read-back)
 * are registered with `route: null` + a `capability.surface`, so they are
 * discoverable (⌘K + a panel) even though they have no `<Route>`.
 */

export type WorkspaceRole =
  | 'admin'
  | 'analyst'
  | 'data_specialist'
  | 'accountant_internal'
  | 'accountant_external'
  | 'attorney'
  | 'attorney_internal'  // on-staff attorney REVIEWER: cross-firm, company staff (DB-provisioned, not a firm seat)
  | 'client'
  // Phase 7 mediation wall: the external NEUTRAL. A separate org_type='mediator'
  // Clerk org (NOT a firm staff role) — gets ONLY the stripped neutral workspace.
  | 'mediator'
  // PR1 (2026-11-04): adjuster seats
  | 'public_adjuster'
  | 'carrier_adjuster';

export type NavGroup =
  | 'work_desk'
  | 'cases'
  | 'review'
  | 'deliverables'
  | 'intelligence'
  | 'admin';

export type LifecycleStage =
  | 'intake'
  | 'data'
  | 'analysis'
  | 'deliverables'
  | 'settlement';

export interface ToolCapability {
  /** Backend service / tool that provides this capability (no `<Route>`). */
  backend: string;
  /** The UI affordance that exposes it (panel id). Required when route is null. */
  surface: string;
  /** Optional section identifier within the surface (e.g., 'statutory-penalties'). */
  section?: string;
}

export interface ToolManifestEntry {
  /** Stable id (kebab-case). Used as the ⌘K key and nav key. */
  id: string;
  /** Human label shown in nav / palette / breadcrumb. */
  label: string;
  /** Extra search terms for the ⌘K palette. */
  synonyms?: string[];
  /** Roles that may see this entry. */
  roles: WorkspaceRole[];
  /** Which left-nav group it lives in. */
  group: NavGroup;
  /** Which in-case lifecycle stage it belongs to (null = portfolio-level). */
  lifecycleStage?: LifecycleStage | null;
  /** App.tsx route path. `null` ONLY for non-routed capabilities. */
  route: string | null;
  /** Backend capability descriptor — REQUIRED when `route` is null (§0.1). */
  capability?: ToolCapability | null;
  /** Requires an open case/report to be meaningful. */
  needsCase?: boolean;
  /** Hidden from client/attorney lenses (carries staff-only $ or internals). */
  staffOnly?: boolean;
  /** Reachable via ⌘K but not a primary nav item (deep / param leaf). */
  paletteOnly?: boolean;
  /** Free-text note (migration status, audit cross-ref). */
  note?: string;
  /** Description of the feature (shown in tooltips, discovery). */
  description?: string;
}

export interface ExcludedRoute {
  route: string;
  reason: string;
}

/* ------------------------------------------------------------------ *
 * FEATURES — every entry is forward-facing (nav group + ⌘K + tab).
 *
 * PRINCIPLE: we surface, we do not delete. Every routed tool was built
 * with purpose; "orphaned" means un-surfaced (a discovery gap), never
 * dead. Legacy / alternate flows stay routed and reachable (often
 * paletteOnly) rather than being removed.
 * ------------------------------------------------------------------ */
export const TOOLS_MANIFEST: ToolManifestEntry[] = [
  /* ---- WorkDesk — Role landing (Phase 3.1) ---------------------- */
  { id: 'workdesk-admin', label: 'Work Desk', roles: ['admin'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/admin', note: 'Phase 3.1 role-based landing' },
  { id: 'workdesk-analyst', label: 'Work Desk', roles: ['analyst'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/analyst', note: 'Phase 3.1 role-based landing' },
  { id: 'workdesk-data-specialist', label: 'Work Desk', roles: ['data_specialist'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/data-specialist', note: 'Phase 3.1 role-based landing' },
  { id: 'workdesk-accountant-internal', label: 'Work Desk', roles: ['accountant_internal'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/accountant-internal', note: 'Phase 3.1 — internal accountant WorkDesk with Queue/My-Cases/Signed-Off tabs + 8 sidebar widgets' },
  { id: 'workdesk-accountant-external', label: 'Work Desk', roles: ['accountant_external'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/accountant-external', note: 'Phase 3.1 — external accountant WorkDesk (consolidated feed: 5 priority buckets + legacy reports list)' },
  { id: 'workdesk-attorney', label: 'Work Desk', roles: ['attorney', 'attorney_internal'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/attorney', note: 'Phase 3.1 role-based landing' },

  /* ---- Tools Hub (Phase 1.1 — entry point for manifest discovery) -------- */
  {
    id: 'tools-hub',
    label: 'Tools Hub',
    synonyms: ['tools', 'hub', 'discovery', 'all tools'],
    roles: ['admin', 'analyst', 'data_specialist', 'accountant_internal', 'accountant_external', 'attorney', 'attorney_internal'],
    group: 'work_desk',
    lifecycleStage: null,
    route: '/tools-hub',
  },

  /* ---- Staff / in-house (Clerk) --------------------------------- */
  // Firm-first admin rail (admin dashboard spec §1/§2): the admin's Operations
  // group leads with Firms → Golden Cases → All Users. (Decluttering the rest of
  // the admin rail per §10/§11 is the next slice; these are additive for now.)
  { id: 'admin-firms', label: 'Firms', synonyms: ['law firms', 'firm'], roles: ['admin'], group: 'work_desk', lifecycleStage: null, route: '/admin/firms' },
  { id: 'admin-golden-cases', label: 'Golden Cases', synonyms: ['demo', 'reference'], roles: ['admin'], group: 'work_desk', lifecycleStage: null, route: '/admin/golden-cases' },
  { id: 'admin-all-users', label: 'All Users', synonyms: ['users', 'directory', 'view as'], roles: ['admin'], group: 'work_desk', lifecycleStage: null, route: '/admin/users' },
  { id: 'work-desk', label: 'Work Desk', synonyms: ['dashboard', 'home'], roles: ['admin', 'analyst', 'data_specialist', 'accountant_internal'], group: 'work_desk', lifecycleStage: null, route: '/dashboard' },
  { id: 'staff-dashboard', label: 'Staff Dashboard', roles: ['admin', 'analyst'], group: 'work_desk', lifecycleStage: null, route: '/staff', staffOnly: true },
  { id: 'work-queue', label: 'Work Queue', synonyms: ['needs review', 'queue'], roles: ['admin', 'analyst', 'data_specialist'], group: 'work_desk', lifecycleStage: null, route: '/queue' },
  { id: 'data-specialist-queue', label: 'Data Specialist Queue', roles: ['data_specialist'], group: 'work_desk', lifecycleStage: null, route: '/workdesk/data-specialist', note: 'FL-5 5.2 — canonical DS surface is the role-parameterized WorkDesk (/workdesk/data-specialist); the frozen /data-specialist/work-queue redirects here' },
  { id: 'closed-cases', label: 'Closed Cases', roles: ['admin', 'analyst'], group: 'cases', lifecycleStage: null, route: '/closed-cases' },

  /* ---- Intake / case creation ----------------------------------- */
  { id: 'new-claim', label: 'New Claim', synonyms: ['new case', 'intake', 'wizard'], roles: ['admin', 'analyst', 'data_specialist'], group: 'cases', lifecycleStage: 'intake', route: '/wizard-v2' },
  { id: 'unified-upload', label: 'Upload Documents', roles: ['admin', 'analyst', 'data_specialist'], group: 'cases', lifecycleStage: 'intake', route: '/wizard-v2' },
  { id: 'bulk-upload', label: 'Bulk Upload', roles: ['admin', 'analyst', 'data_specialist'], group: 'cases', lifecycleStage: 'intake', route: '/bulk-upload' },
  { id: 'new-claim-legacy', label: 'New Claim (classic)', roles: ['admin', 'analyst'], group: 'cases', lifecycleStage: 'intake', route: '/new-claim', paletteOnly: true, note: 'classic intake flow; /wizard-v2 is the primary path' },
  { id: 'new-case-v2', label: 'New Case Wizard', roles: ['admin', 'analyst'], group: 'cases', lifecycleStage: 'intake', route: '/new-case-v2', paletteOnly: true, note: 'alternate wizard flow; kept routed alongside /wizard-v2' },
  { id: 'case-status', label: 'Case Status', roles: ['admin', 'analyst', 'data_specialist'], group: 'cases', lifecycleStage: 'intake', route: '/case/:claimId', needsCase: true },
  { id: 'case-documents', label: 'Case Documents', roles: ['admin', 'analyst', 'data_specialist'], group: 'cases', lifecycleStage: 'intake', route: '/case/:claimId/documents', needsCase: true, paletteOnly: true },

  /* Phase 4 Case Workspace stage routes are EXCLUDED, not manifest entries:
     the tabbed stages are sub-navigation within CaseWorkspaceShell (reached via
     WorkspaceTabBar), not independent ⌘K destinations. See EXCLUDED_ROUTES. */

  /* ---- Data stage ----------------------------------------------- */
  { id: 'data-review', label: 'Data Review', roles: ['data_specialist', 'analyst'], group: 'review', lifecycleStage: 'data', route: '/review' },
  { id: 'transaction-review-queue', label: 'Transaction Review', roles: ['data_specialist', 'analyst'], group: 'review', lifecycleStage: 'data', route: '/review-queue', note: 'Phase 5.3 — redirects to /queue (unified work queue)' },
  { id: 'cleanup-validation', label: 'Cleanup Validation', roles: ['data_specialist'], group: 'review', lifecycleStage: 'data', route: '/cleanup-validation', note: 'data-cleanup validation surface' },
  { id: 'cleanup-validation-job', label: 'Cleanup Validation (job)', roles: ['data_specialist'], group: 'review', lifecycleStage: 'data', route: '/cleanup/validation/:jobId', needsCase: true, paletteOnly: true },
  { id: 'batch-processing', label: 'Batch OCR Processing', synonyms: ['textract', 'ocr', 'batch'], roles: ['admin', 'data_specialist'], group: 'admin', lifecycleStage: 'data', route: '/batch-processing', note: 'ORPHAN surfaced — was zero inbound links + no layout (audit §5)' },
  { id: 'issue-tracker', label: 'Issue Tracker', roles: ['admin', 'data_specialist', 'analyst'], group: 'review', lifecycleStage: 'data', route: '/issues', note: 'Phase 5.3 — redirects to /queue (unified work queue)' },
  { id: 'issue-detail', label: 'Issue Detail', roles: ['admin', 'data_specialist', 'analyst'], group: 'review', lifecycleStage: 'data', route: '/issues/:issueId', needsCase: true, paletteOnly: true },

  /* ---- Analysis stage — Phase 4.4 tab system -------------------------------- */
  { id: 'analysis-stage', label: 'Analysis Stage', roles: ['admin', 'analyst', 'attorney', 'attorney_internal'], group: 'review', lifecycleStage: 'analysis', route: '/analysis/:reportId', needsCase: true, note: 'Phase 4.4 — Analysis stage tab system with 6 panels' },

  /* ---- Analysis stage tabs (hash-based deep-links) -------------------------- */
  { id: 'report-detail', label: 'Report', roles: ['admin', 'analyst'], group: 'cases', lifecycleStage: 'analysis', route: '/report/:id', needsCase: true },
  { id: 'adjuster-analyzer', label: 'Adjuster Analyzer', roles: ['admin', 'analyst'], group: 'review', lifecycleStage: 'analysis', route: '/adjuster/:reportId', needsCase: true },
  { id: 'staff-war-room', label: 'War Room', synonyms: ['deliberation', 'agents'], roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: 'analysis', route: '/deliberation/:reportId', needsCase: true },
  { id: 'narrator', label: 'Narrator', roles: ['admin', 'analyst'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/narrator/:id', needsCase: true, paletteOnly: true },

  /* ---- Settlement stage ----------------------------------------- */
  { id: 'roadmap', label: 'Case Strengthening Roadmap', roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: 'settlement', route: '/roadmap/:reportId', needsCase: true, staffOnly: true, note: 'STAFF-ONLY $ figures' },
  { id: 'compare-reports', label: 'Compare Reports', roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: 'settlement', route: '/compare' },
  { id: 'analytics', label: 'Analytics', roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: null, route: '/analytics' },
  { id: 'vendor-intel', label: 'Vendor Intel', roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: 'settlement', route: '/vendor-intelligence' },
  { id: 'game-theory', label: 'Game Theory', roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: 'settlement', route: '/game-theory' },
  { id: 'settlement-intel', label: 'Settlement Intel', roles: ['admin', 'analyst'], group: 'intelligence', lifecycleStage: 'settlement', route: '/settlement-intelligence' },

  /* ---- Phase 4.7 — Settlement Tab with Sub-Navigation -------- */
  { id: 'settlement-tab', label: 'Settlement', synonyms: ['sensitivity', 'historical', 'what-if', 'strategy', 'litigation', 'bad faith', 'game theory', 'case law', 'evidence', 'roadmap', 'texas 542', 'subrogation'], roles: ['admin', 'analyst', 'attorney', 'attorney_internal'], group: 'intelligence', lifecycleStage: 'settlement', route: '/case/:id/settlement', needsCase: true, note: 'Redirects to the attorney workspace Settlement stage (/attorney/case/:id/settlement); the legacy sidebar SettlementPage was deprecated (its per-tool adapters 404/422 on the current data model).' },

  /* ---- Admin ---------------------------------------------------- */
  { id: 'settings', label: 'Settings', roles: ['admin', 'analyst', 'data_specialist', 'accountant_internal'], group: 'admin', lifecycleStage: null, route: '/settings' },
  { id: 'activity-log', label: 'Activity Log', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/settings/activity', staffOnly: true },
  // System group (admin dashboard §9) — admin tools promoted out of the Settings
  // 2nd sidebar into the rail. Admin-only; other seats keep their own surfaces.
  { id: 'admin-leads', label: 'Leads', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/admin/leads' },
  { id: 'admin-team-members', label: 'Team Members', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/admin/team' },
  { id: 'admin-feature-flags', label: 'Feature Flags', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/admin/feature-flags' },
  { id: 'admin-data-health', label: 'Data Health', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/admin/data-health' },
  { id: 'admin-error-logs', label: 'Error Logs', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/admin/error-logs' },
  { id: 'pattern-admin', label: 'Pattern Admin', roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/settings/patterns', staffOnly: true },
  { id: 'orchestrator', label: 'Orchestrator', synonyms: ['pipeline', 'dispatch'], roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/orchestrator', staffOnly: true },
  { id: 'fleet-hud', label: 'Fleet HUD', synonyms: ['fleet', 'orchestrator hud', 'runs', 'kairos'], roles: ['admin'], group: 'admin', lifecycleStage: null, route: '/fleet', staffOnly: true },
  /* docs-guide re-homed to client portal as /client/prep-guide and /portal/:token/prep-guide (Phase 5.2) */

  /* ---- In-house accountant per-case tools ----------------------- */
  { id: 'accountant-workdesk', label: 'Accountant Work', roles: ['accountant_internal'], group: 'work_desk', lifecycleStage: null, route: '/dashboard/accountant' },
  { id: 'recon-workbench', label: 'P&L Reconciliation Workbench', roles: ['accountant_internal'], group: 'review', lifecycleStage: 'data', route: '/dashboard/accountant/workbench/:reportId', needsCase: true },
  { id: 'export-center-internal', label: 'Export Center', synonyms: ['bates', 'exhibit', 'iso cp 00 30'], roles: ['accountant_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/dashboard/accountant/export/:reportId', needsCase: true },
  { id: 'engine-review-internal', label: 'Engine Review', roles: ['accountant_internal'], group: 'review', lifecycleStage: 'analysis', route: '/dashboard/accountant/review/:reportId', needsCase: true },
  { id: 'methodology-internal', label: 'Methodology Comparison', synonyms: ['daubert'], roles: ['accountant_internal'], group: 'review', lifecycleStage: 'analysis', route: '/dashboard/accountant/methodology/:reportId', needsCase: true },
  { id: 'analysis-tools-internal', label: 'Analysis Tools / Stress Test', synonyms: ['monte carlo', 'sensitivity'], roles: ['accountant_internal'], group: 'review', lifecycleStage: 'analysis', route: '/dashboard/accountant/analysis/:reportId', needsCase: true },
  { id: 'engine-review-detail-internal', label: 'Engine Review (detail)', roles: ['accountant_internal'], group: 'review', lifecycleStage: 'analysis', route: '/dashboard/accountant/review/:reportId/:engineId', needsCase: true, paletteOnly: true },
  { id: 'engine-review-edit-internal', label: 'Engine Review (edit)', roles: ['accountant_internal'], group: 'review', lifecycleStage: 'analysis', route: '/dashboard/accountant/review/:reportId/:engineId/edit', needsCase: true, paletteOnly: true },

  /* ---- Phase 4.5 — Accountant Analysis Tab with Sub-Navigation -------- */
  { id: 'accountant-analysis-tab', label: 'Analysis', synonyms: ['accountant lens', 'engine review', 'methodology', 'stress test'], roles: ['accountant_internal', 'accountant_external'], group: 'review', lifecycleStage: 'analysis', route: '/reports/:reportId/analysis', needsCase: true, note: 'Phase 4.5 — Analysis stage with Accountant Lens sub-navigation (4 sub-panels)' },

  /* ---- Attorney (JWT) ------------------------------------------- */
  /* ---- Attorney WorkDesk (Phase 3.1 consolidation — replaces dashboard/portfolio/queue) */
  { id: 'attorney-workdesk', label: 'Work Desk', roles: ['attorney', 'attorney_internal'], group: 'work_desk', lifecycleStage: null, route: '/attorney/workdesk', note: 'Phase 3.1 — consolidated attorney surface (was dashboard/portfolio/queue)' },
  { id: 'attorney-dashboard', label: 'Dashboard', roles: ['attorney', 'attorney_internal'], group: 'work_desk', lifecycleStage: null, route: '/attorney/dashboard', paletteOnly: true, note: 'redirects to /attorney/workdesk' },
  { id: 'attorney-home', label: 'Attorney Home', roles: ['attorney', 'attorney_internal'], group: 'work_desk', lifecycleStage: null, route: '/attorney', paletteOnly: true, note: 'redirects to /attorney/workdesk' },
  { id: 'attorney-portfolio', label: 'Portfolio', roles: ['attorney', 'attorney_internal'], group: 'work_desk', lifecycleStage: null, route: '/attorney/portfolio', paletteOnly: true, note: 'redirects to /attorney/workdesk' },
  { id: 'attorney-case', label: 'Case', roles: ['attorney', 'attorney_internal'], group: 'cases', lifecycleStage: 'intake', route: '/attorney/case/:caseId', needsCase: true },
  { id: 'attorney-case-documents', label: 'Case Documents', roles: ['attorney', 'attorney_internal'], group: 'cases', lifecycleStage: 'intake', route: '/attorney/case/:caseId/documents', needsCase: true, paletteOnly: true },

  /* Phase 4 Attorney Case Workspace stage routes: EXCLUDED (TabBar sub-nav). */
  { id: 'gen-demand-letter', label: 'Demand Letter', synonyms: ['generator'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/case/:caseId/demand-letter', needsCase: true },
  { id: 'gen-proof-of-loss', label: 'Proof of Loss', synonyms: ['sworn', 'generator'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/case/:caseId/proof-of-loss', needsCase: true },
  { id: 'gen-euo-prep', label: 'EUO Prep Memo', synonyms: ['generator'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/case/:caseId/euo-prep-memo', needsCase: true },
  { id: 'gen-ror-response', label: 'RoR Response', synonyms: ['reservation of rights', 'generator'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/case/:caseId/ror-response', needsCase: true },
  { id: 'gen-ror-analysis', label: 'RoR Analysis Memo', synonyms: ['coverage analysis', 'generator'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/case/:caseId/ror-analysis', needsCase: true },
  { id: 'bi-timeline', label: 'BI Litigation Timeline', roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/case/:caseId/timeline', needsCase: true },
  { id: 'attorney-war-room', label: 'War Room', synonyms: ['challenge', 'grounded q&a'], roles: ['attorney', 'attorney_internal'], group: 'intelligence', lifecycleStage: 'analysis', route: '/attorney/war-room/:reportId', needsCase: true, note: 'ORPHAN surfaced — was zero inbound links (audit §5)' },
  { id: 'attorney-overlay', label: 'Overlay Editor', synonyms: ['work product'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/attorney/overlay/:reportId', needsCase: true, note: 'ORPHAN surfaced — was zero inbound links (audit §5)' },

  /* ---- Attorney case-less generators (plan §5 — case-picker group) ---
     These reach each generator WITHOUT first opening a case (the generator
     prompts for a case). Built with purpose; surfaced as the top-level
     Generators group rather than treated as dead aliases. */
  { id: 'gen-demand-letter-picker', label: 'Demand Letter', synonyms: ['generator', 'new demand'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: null, route: '/attorney/demand-letter' },
  { id: 'gen-proof-of-loss-picker', label: 'Proof of Loss', synonyms: ['generator', 'sworn'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: null, route: '/attorney/proof-of-loss' },
  { id: 'gen-euo-prep-picker', label: 'EUO Prep Memo', synonyms: ['generator'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: null, route: '/attorney/euo-prep-memo' },
  { id: 'gen-ror-response-picker', label: 'RoR Response', synonyms: ['generator', 'reservation of rights'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: null, route: '/attorney/ror-response' },
  { id: 'gen-ror-analysis-picker', label: 'RoR Analysis Memo', synonyms: ['generator', 'coverage analysis'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: null, route: '/attorney/ror-analysis' },
  { id: 'bi-timeline-picker', label: 'BI Litigation Timeline', synonyms: ['generator', 'timeline'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: null, route: '/attorney/timeline' },

  /* ---- Phase 4.6 — Top-level Generators group (case-picker, no open case required) */
  { id: 'generators', label: 'Generators', synonyms: ['document generators', 'demand letter', 'proof of loss', 'euo', 'ror'], roles: ['attorney', 'attorney_internal'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/generators', note: 'Phase 4.6 — Top-level Generators group with case-picker' },

  /* ---- External accountant portal (JWT) — Phase 3.1 CONSOLIDATED ------------------------ */
  /* The external accountant WorkDesk at /workdesk/accountant-external now combines
     accountant-home (5 priority buckets) + accountant-reports (legacy list) into a
     unified feed. The old routes remain reachable via redirect/palette. */
  { id: 'accountant-home', label: 'Home', roles: ['accountant_external'], group: 'work_desk', lifecycleStage: null, route: '/accountant/home', paletteOnly: true, note: 'Phase 3.1 — redirects to /workdesk/accountant-external' },
  { id: 'accountant-reports', label: 'Reports', roles: ['accountant_external'], group: 'cases', lifecycleStage: null, route: '/accountant/reports', paletteOnly: true, note: 'Phase 3.1 — redirects to /workdesk/accountant-external' },
  { id: 'accountant-report-view', label: 'Report', roles: ['accountant_external'], group: 'cases', lifecycleStage: 'analysis', route: '/accountant/reports/:reportId', needsCase: true, paletteOnly: true, note: 'Rail link navigated to the literal :reportId; reached via the report list instead' },

  /* Phase 4 Accountant Case Workspace stage routes: EXCLUDED (TabBar sub-nav). */
  { id: 'accountant-comments', label: 'Comments', roles: ['accountant_external'], group: 'review', lifecycleStage: null, route: '/accountant/comments' },
  { id: 'accountant-profile', label: 'Profile', roles: ['accountant_external'], group: 'admin', lifecycleStage: null, route: '/accountant/profile' },
  { id: 'anomaly-review', label: 'Anomaly Review', roles: ['accountant_internal'], group: 'review', lifecycleStage: 'data', route: '/dashboard/accountant/anomalies/:reportId', needsCase: true, note: 'Linked from internal WorkDesk + AccountantDashboard CTAs (2026-07-28). Endpoints are Clerk-gated (get_current_user), so this is in-house only; the legacy /accountant/anomalies/:reportId route remains but 401s for external-JWT CPAs.' },
  { id: 'reconciliation-external', label: 'Reconciliation', roles: ['accountant_external'], group: 'review', lifecycleStage: 'data', route: '/accountant/reconciliation/:reportId', needsCase: true, paletteOnly: true, note: 'Template route — reached via the case workspace stage strip, not the rail' },
  { id: 'engine-review-external', label: 'Engine Review & Attest', synonyms: ['finalize', 'cp 04 11', 'sign off', 'attestation'], roles: ['accountant_external'], group: 'review', lifecycleStage: 'analysis', route: '/accountant/review/:reportId', needsCase: true, paletteOnly: true, note: 'ORPHAN surfaced — the CP 04 11 sign-off gate, was zero inbound links (audit §5). Template route — reached via the case workspace stage strip, not the rail' },
  { id: 'engine-review-detail-external', label: 'Engine Review (detail)', roles: ['accountant_external'], group: 'review', lifecycleStage: 'analysis', route: '/accountant/review/:reportId/:engineId', needsCase: true, paletteOnly: true },
  { id: 'export-center-external', label: 'Export Center', roles: ['accountant_external'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/accountant/case/:reportId/export', needsCase: true, paletteOnly: true, note: 'Template route — reached via the case workspace stage strip, not the rail' },
  { id: 'accountant-narrator', label: 'Narrator', roles: ['accountant_external'], group: 'deliverables', lifecycleStage: 'deliverables', route: '/accountant/narrator/:reportId', needsCase: true, paletteOnly: true },

  /* ---- Co-pilot panel (§5b.12 — what-if / run-diff / scenario / accuracy) ----- */
  {
    id: 'ai-copilot',
    label: 'AI Co-pilot',
    synonyms: ['what-if', 'run diff', 'scenario', 'engine accuracy', 'assistant', 'chat', 'ask the co-pilot'],
    roles: ['accountant_internal', 'accountant_external', 'analyst', 'attorney', 'attorney_internal'],
    group: 'intelligence',
    lifecycleStage: 'analysis',
    route: '/copilot',
    capability: { backend: 'intelligence_tools', surface: 'copilot-panel' },
    note: '§5b.12 — persistent panel in accountant/War Room; what-if / run-diff / scenario / accuracy'
  },
  { id: 'engine-audit', label: 'Engine Audit', synonyms: ['show every engine', 'transparency', 'provenance', 'freshness'], roles: ['accountant_internal', 'accountant_external', 'analyst'], group: 'review', lifecycleStage: 'analysis', route: '/engine-audit/:reportId', capability: { backend: 'engine_outputs.get_all_engines', surface: 'engine-audit-panel' }, needsCase: true, paletteOnly: true, note: '§5b.13 — all ~20 engines: output·version·freshness·CONTESTED·diff·accuracy·audit chain. Template route — reached via the case workspace stage strip, not the rail' },

  /* ---- Texas §542 Statutory Penalties (embedded card — no route) ---------------- */
  {
    id: 'texas-542-statutory-penalties',
    label: 'Texas §542 Statutory Penalties',
    description: 'Prompt-pay penalty interest calculator — 18% per annum vs carrier',
    synonyms: ['prompt pay', 'penalty interest', 'texas insurance code', '542'],
    roles: ['attorney', 'attorney_internal', 'analyst'],
    group: 'intelligence',
    lifecycleStage: 'settlement',
    route: null,
    capability: { backend: 'texas_542_calculator.compute_texas_542', surface: 'settlement', section: 'statutory-penalties' },
    needsCase: true,
  },

  /* ---- SHAP Explainability — "Why flagged?" drawer (no route, panel surface) ----- */
  {
    id: 'shap-explainability',
    label: 'Why flagged?',
    description: 'SHAP explainability for anomaly attribution — understand why a transaction or pattern was flagged',
    synonyms: ['shap', 'explainability', 'feature importance', 'why flagged', 'anomaly explanation', 'model interpretability'],
    roles: ['analyst', 'accountant_internal', 'accountant_external'],
    group: 'review',
    lifecycleStage: 'data',
    route: null,
    capability: { backend: 'explainability', surface: 'shap-drawer' },
    needsCase: true,
  },

  /* ---- Data Quality Report — Great Expectations / Pandera (panel, no route) ----- */
  {
    id: 'data-quality-report',
    label: 'Data Quality Report',
    description: 'Great Expectations and Pandera data quality validation — statistical profile, expectations pass/fail, cross-document validation',
    synonyms: ['data quality', 'great expectations', 'pandera', 'validation', 'profiling', 'cross-validation'],
    roles: ['data_specialist', 'analyst'],
    group: 'review',
    lifecycleStage: 'data',
    route: null,
    capability: { backend: 'data_quality', surface: 'data-quality-panel' },
    needsCase: true,
  },

  /* ---- Audit Chain — Tamper-evident integrity verification (panel, no route) ----- */
  {
    id: 'audit-chain-verify',
    label: 'Verify Integrity',
    description: 'Cryptographic audit chain verification — tamper-evident hash chain integrity for forensic defensibility',
    synonyms: ['audit chain', 'verify integrity', 'tamper evident', 'hash chain', 'blockchain', 'forensic audit'],
    roles: ['accountant_external', 'attorney', 'attorney_internal', 'accountant_internal', 'analyst'],
    group: 'review',
    lifecycleStage: 'analysis',
    route: null,
    capability: { backend: 'audit_chain', surface: 'engine-audit-panel', section: 'integrity' },
    needsCase: true,
  },

  /* ---- Deliverables Tab — Phase 4.6 in-tab workbench (panel, no route) -----------
     The DeliverablesTab component provides a sub-rail with generators, overlay
     editor, BI timeline, export center, expert report, narrator, and package view.
     It's accessed via the CaseWorkspaceShell /deliverables stage route. */
  {
    id: 'deliverables-tab',
    label: 'Deliverables',
    description: 'Deliverables stage with in-tab workbench — generators, overlay editor, BI timeline, export center, expert report, narrator, package view',
    synonyms: ['deliverables', 'generators', 'documents', 'exports'],
    roles: ['attorney', 'attorney_internal', 'analyst'],
    group: 'deliverables',
    lifecycleStage: 'deliverables',
    route: null,
    capability: { backend: 'deliverables_service', surface: 'deliverables-panel' },
    needsCase: true,
  },

  /* ---- WorkspaceShell — Unified layout infrastructure (Phase 2.1) ----------------
     This is a layout/infrastructure component with no route. It replaces
     5 legacy layouts: AppLayout, AttorneyLayout, AccountantLayout,
     AttorneyRoleLayout, RoleDashboardLayout. Documented here for
     anti-orphan compliance (route:null infrastructure entry). */
  {
    id: 'workspace-shell',
    label: 'Workspace Shell',
    description: 'Unified layout shell replacing 5 legacy layouts — left rail + topbar navigation infrastructure',
    roles: ['admin', 'analyst', 'data_specialist', 'accountant_internal', 'accountant_external', 'attorney', 'attorney_internal'],
    group: 'admin',
    lifecycleStage: null,
    route: null,
    capability: { backend: 'infrastructure', surface: 'layout-shell' },
    note: 'Infrastructure — no route; replaces AppLayout/AttorneyLayout/AccountantLayout/AttorneyRoleLayout/RoleDashboardLayout',
  },

  /* ---- RoleRoute — Route authorization guard (Phase 2.2) -------------------------
     A route guard component that restricts access by WorkspaceRole.
     Not a routed page itself; a capability used by other routes.
     Wired to /orchestrator, /settings/activity, /settings/patterns. */
  {
    id: 'role-route-guard',
    label: 'Role Route Guard',
    description: 'Route authorization guard — restricts page access to specific WorkspaceRoles (admin-only for sensitive routes)',
    roles: ['admin', 'analyst', 'data_specialist', 'accountant_internal', 'accountant_external', 'attorney', 'attorney_internal'],
    group: 'admin',
    lifecycleStage: null,
    route: null,
    capability: { backend: 'infrastructure', surface: 'route-guard' },
    note: 'Infrastructure guard — no route; used by /orchestrator, /settings/activity, /settings/patterns',
  },

  /* ---- Client Portal — Phase 5: Unified Client Portal (reduced lens) ----------
     The unified client portal provides a reduced-lens view for clients:
     only Intake-stage tabs (Status, Documents, Clarifications) scoped to
     their own case via magic-link JWT authentication. */

  /* Unified Client Portal — the primary client-facing surface (Phase 5.1) */
  {
    id: 'client-portal-unified',
    label: 'Client Portal',
    synonyms: ['upload', 'documents', 'clarifications', 'my case', 'client'],
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/client',
    needsCase: true,
    note: 'Phase 5.1 — Unified client portal with reduced lens (Intake tabs: Upload, Clarify, Overview)',
  },

  /* Client Status — Intake-stage status tracker (Phase 5.1) */
  {
    id: 'client-status',
    label: 'Case Status',
    synonyms: ['status', 'progress', 'where is my case'],
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/client/status',
    needsCase: true,
    paletteOnly: true,
    note: 'Phase 5.2 — Client case status tracker (Intake tab) with progress metrics and timeline',
  },

  /* Client Prep-Guide — Documentation guide for clients (Phase 5.2) */
  {
    id: 'client-prep-guide',
    label: 'Prep Guide',
    synonyms: ['docs guide', 'documentation', 'what to upload', 'help'],
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/client/prep-guide',
    needsCase: true,
    paletteOnly: true,
    note: 'Phase 5.2 — Client documentation guide (re-homed from staff /docs-guide)',
  },

  /* Client Portal Status — Token-gated status view (Phase 5.2) */
  {
    id: 'portal-status',
    label: 'Portal Status',
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/portal/:token/status',
    needsCase: true,
    paletteOnly: true,
    note: 'Phase 5.2 — Token-gated client status view',
  },

  /* Client Portal Prep-Guide — Token-gated prep guide (Phase 5.2) */
  {
    id: 'portal-prep-guide',
    label: 'Portal Prep Guide',
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/portal/:token/prep-guide',
    needsCase: true,
    paletteOnly: true,
    note: 'Phase 5.2 — Token-gated client prep guide (re-homed from staff /docs-guide)',
  },

  /* Client Documents — Intake-stage document upload (Phase 5.1) */
  {
    id: 'client-documents',
    label: 'Upload Documents',
    synonyms: ['upload', 'files', 'documents'],
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/client/upload',
    needsCase: true,
    paletteOnly: true,
    note: 'Phase 5.1 — Client document upload surface (Intake tab)',
  },

  /* Client Clarifications — Intake-stage transaction clarifications (Phase 5.1) */
  {
    id: 'client-clarifications',
    label: 'Clarifications',
    synonyms: ['clarify', 'transactions', 'categorize', 'questions'],
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: '/client/clarify',
    needsCase: true,
    paletteOnly: true,
    note: 'Phase 5.1 — Client transaction clarification surface (Intake tab)',
  },

  /* Legacy client upload portal — capability-only when unified is enabled */
  {
    id: 'client-upload-legacy',
    label: 'Legacy Upload Portal',
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: null,
    capability: { backend: 'client_portal', surface: 'upload-legacy' },
    needsCase: true,
    note: 'Legacy client upload — surfaced via redirect when CLIENT_PORTAL_UNIFIED=true',
  },

  /* Legacy client clarification portal — capability-only when unified is enabled */
  {
    id: 'client-clarify-legacy',
    label: 'Legacy Clarification Portal',
    roles: ['client'],
    group: 'cases',
    lifecycleStage: 'intake',
    route: null,
    capability: { backend: 'client_portal', surface: 'clarify-legacy' },
    needsCase: true,
    note: 'Legacy client clarification — surfaced via redirect when CLIENT_PORTAL_UNIFIED=true',
  },
];

/* ------------------------------------------------------------------ *
 * EXCLUDED — routes that legitimately do NOT belong in feature nav.
 * Every App.tsx / accountant-routes path must be here or in the manifest.
 * ------------------------------------------------------------------ */
export const EXCLUDED_ROUTES: ExcludedRoute[] = [
  /* Public marketing */
  { route: '/home', reason: 'public marketing page' },
  { route: '/about', reason: 'public marketing page' },
  { route: '/contact', reason: 'public marketing page' },
  { route: '/privacy', reason: 'public marketing page' },
  { route: '/terms', reason: 'public marketing page' },
  { route: '/waitlist', reason: 'public marketing page' },
  { route: '/', reason: 'root — ConditionalHome (marketing or role-land redirect)' },
  /* Phase 7 mediation wall — the external NEUTRAL's only surface (mediator role, no firm nav) */
  { route: '/mediation-wall', reason: 'Phase 7 neutral workspace (mediator role); not a firm feature-nav tool' },
  { route: '/mediation-wall/:mediationId', reason: 'Phase 7 neutral workspace (mediator role); not a firm feature-nav tool' },
  { route: '/preview', reason: 'redesign preview hub — links to every /preview/* design-review surface; not a shipped tool' },
  { route: '/preview/workdesk', reason: 'redesign preview surface — pixel-faithful Daylight Work Desk for design review; not a shipped tool' },
  { route: '/preview/client-portal', reason: 'redesign preview surface — pixel-faithful Client Portal (light+dark) for design review; not a shipped tool' },
  { route: '/preview/attorney-dashboard', reason: 'redesign preview surface — Attorney Dashboard (light+dark) for design review; not a shipped tool' },
  { route: '/preview/verification-layer', reason: 'Phase 1 verification-layer preview — all five verification components with Summit Ridge fixtures for design/owner review; not a shipped tool' },
  { route: '/preview/case-path', reason: 'Phase 2 case-path shell preview — internal eyes-on surface, not a client tool' },
  { route: '/preview/data-specialist', reason: 'redesign preview surface — Data Specialist Dashboard (light+dark) for design review; not a shipped tool' },
  { route: '/preview/war-room', reason: 'redesign preview surface — War Room (light+dark) for design review; not a shipped tool' },
  { route: '/preview/war-room/:reportId', reason: 'convenience redirect → /deliberation/:reportId (users deep-link the preview path expecting the live War Room)' },
  { route: '/war-room/:reportId', reason: 'convenience redirect → /deliberation/:reportId (the natural guess for the live War Room URL)' },
  { route: '/accountant/anomalies/:reportId', reason: 'legacy alias for Anomaly Review — canonical route is /dashboard/accountant/anomalies/:reportId (in-house silo; the /dashboard/anomaly-review/* endpoints are Clerk-only so external-JWT CPAs 401 here). Kept for old bookmarks pending the dual-auth decision (2026-07-28 worklist).' },
  { route: '/preview/document-hub', reason: 'redesign preview surface — Document Hub (light+dark) for design review; not a shipped tool' },
  { route: '/preview/admin-orchestrator', reason: 'redesign preview surface — Admin Orchestrator (light+dark) for design review; not a shipped tool' },
  { route: '/preview/external-accountant', reason: 'redesign preview surface — External Accountant Portal (light+dark) for design review; not a shipped tool' },

  /* Contextual per-case reconciliation tools — reached from within a case surface via
     CaseMatcherLink / CaseReconcileLink (adjuster + data-specialist case pages, accountant
     workbench), not independent nav / ⌘K destinations. Case-scoped (:reportId). */
  { route: '/matcher/:reportId', reason: 'Item Matcher (property line-item matching workbench) — reached via CaseMatcherLink on the case surfaces, not a top-level nav/⌘K destination' },
  { route: '/reconcile/:reportId', reason: 'two-sided adjuster/contractor manual reconciler — reached via CaseReconcileLink on the case surfaces, not a top-level nav/⌘K destination' },
  { route: '/mediation/:reportId', reason: 'mediation settlement view — reached contextually from the case/settlement surface, not a top-level nav/⌘K destination' },

  /* Auth (no nav — pre-login) */
  { route: '/sign-in', reason: 'auth screen' },
  { route: '/sign-up', reason: 'auth screen' },
  { route: '/sign-out', reason: 'auth action — signs out and redirects to /sign-in' },
  { route: '/logout', reason: 'auth action — alias of /sign-out' },
  { route: '/attorney/login', reason: 'auth screen' },
  { route: '/attorney/reset-password', reason: 'auth screen' },
  { route: '/attorney/forgot-password', reason: 'auth screen' },
  { route: '/accountant/login', reason: 'auth screen' },
  { route: '/accountant/register', reason: 'auth screen' },
  { route: '/accountant/accept-invite/:token', reason: 'invitation onboarding (token-gated)' },

  /* Public token-gated entry points (magic link — intentionally no nav) */
  { route: '/share/:shareToken', reason: 'public share view — its URL token IS the auth' },
  { route: '/upload/:reportId', reason: 'client magic-link upload portal — redirects to unified when CLIENT_PORTAL_UNIFIED=true' },
  { route: '/clarify/:token', reason: 'client magic-link clarification portal — redirects to unified when CLIENT_PORTAL_UNIFIED=true' },
  { route: '/portal/:token', reason: 'unified client portal (token-gated)' },
  { route: '/portal/:token/status', reason: 'client portal status tab — token-gated' },
  { route: '/portal/:token/prep-guide', reason: 'client portal prep-guide tab — token-gated' },
  { route: '/client', reason: 'unified client portal — REAL manifest entry (client-portal-unified)' },
  { route: '/client/status', reason: 'client status tab — REAL manifest entry (client-status)' },
  { route: '/client/prep-guide', reason: 'client prep-guide tab — REAL manifest entry (client-prep-guide)' },
  { route: '/attorney/package/:token', reason: 'attorney package view — URL token IS the auth' },

  /* Demo / dev */
  { route: '/demo', reason: 'public demo' },
  { route: '/demo-narrator', reason: 'public demo' },
  { route: '/demo-wizard', reason: 'public demo' },
  { route: '/demo-report/:id', reason: 'public demo' },
  { route: '/csv-features', reason: 'public demo' },

  /* Removed / Re-homed routes */
  { route: '/docs-guide', reason: 're-homed to client portal as /client/prep-guide and /portal/:token/prep-guide (Phase 5.2)' },

  /* Redirect-only routes (render <Navigate/>) */
  { route: '/wizard', reason: 'redirect → /wizard-v2' },
  { route: '/cleanup', reason: 'redirect → /wizard-v2' },
  { route: '/upload', reason: 'retired standalone quick-parse page (had no claim in scope, silently discarded reviewed data on confirm) — redirect → /wizard-v2, which persists + calculates' },
  { route: '/queue/accountant', reason: 'redirect → /dashboard/accountant?tab=queue' },
  /* Phase 5.3 — Legacy queue routes redirect to unified work queue */
  { route: '/queue/data-specialist', reason: 'redirect → /queue (shared unified work queue)' },
  /* FL-5 5.2 (2026-09-05) — legacy DS landings redirect to the canonical WorkDesk */
  { route: '/dashboard/data-specialist', reason: 'redirect → /workdesk/data-specialist (WorkDesk role=data_specialist)' },
  { route: '/data-specialist/work-queue', reason: 'redirect → /workdesk/data-specialist (frozen UnifiedWorkQueue retired)' },
  { route: '/attorney/case/:caseId/tools', reason: 'redirect → case detail #bad-faith' },
  { route: '/accountant/dashboard', reason: 'redirect → /dashboard/accountant' },
  { route: '/accountant', reason: 'redirect → /dashboard/accountant?tab=queue' },
  { route: '/accountant/report/:reportId', reason: 'redirect → /accountant/reports/:reportId (plural)' },
  /* Phase 3.1 attorney workdesk consolidation — old routes redirect to new canonical */
  { route: '/queue/attorney', reason: 'redirect → /attorney/workdesk (consolidated WorkDesk)' },
  { route: '/attorney/letterhead', reason: 'attorney letterhead settings page (MyLetterhead, task #146) — reached from the attorney profile/settings surface, not a standalone nav/⌘K tool' },

  /* NOTE: the case-less generator routes (/attorney/demand-letter, etc.)
     are NOW in TOOLS_MANIFEST as the case-picker Generators group — they
     are forward-facing features, not excluded. */

  /* Phase 4 Case Workspace shell — parameterized lifecycle-stage routes.
     The case itself is the discoverable feature (case-status / WorkDesk); the
     :stage and :stage/:panel segments are sub-navigation within
     CaseWorkspaceShell, driven by WorkspaceTabBar — not independent nav/⌘K
     destinations, so they are excluded rather than manifest entries. */
  { route: '/case/:reportId/path', reason: 'Live Case Path shell (GAP-P3D) — reached from case links; nav/manifest entry is a Phase 3.5 product decision' },
  { route: '/case/:claimId/:stage', reason: 'Case Workspace shell — stage tab (TabBar sub-nav)' },
  { route: '/case/:claimId/:stage/:panel', reason: 'Case Workspace shell — stage sub-panel' },

  /* The Figure Co. redesign — non-nav destinations (2026-10) */
  { route: '/my-reports', reason: 'reporter\'s own support tickets — reached via the AccountMenu "My reports" link, not feature nav/⌘K' },
  { route: '/for-carriers', reason: 'public carrier court-exhibit marketing landing — pre-login, no nav' },
  { route: '/for-carriers/start', reason: 'carrier landing start CTA — public, pre-login, no nav' },
  { route: '/admin/firms/:id', reason: 'admin firm-detail (firm → cases) — drilled into from the Firms list, not an independent nav/⌘K destination' },
  { route: '/guest/:token', reason: 'guest case-review (scoped-to-one-case magic link) — URL token IS the auth, no nav' },
  { route: '/case/:claimId/ledger', reason: 'ClaimLedger — transaction verify/attest workspace; nav entry point pending K3 design' },
  { route: '/attorney/case/:caseId/:stage', reason: 'Attorney Case Workspace shell — stage tab' },
  { route: '/attorney/case/:caseId/:stage/:panel', reason: 'Attorney Case Workspace shell — stage sub-panel' },
  { route: '/accountant/reports/:reportId/:stage', reason: 'Accountant Case Workspace shell — stage tab' },
  { route: '/accountant/reports/:reportId/:stage/:panel', reason: 'Accountant Case Workspace shell — stage sub-panel' },
  /* FL-4 / FL-5 role Case Workspace shells (adjuster + data-specialist).
     Same shape as the attorney/accountant shells above: the case is reached
     from the role WorkDesk; :stage and :stage/:panel are CaseWorkspaceShell
     sub-navigation, not standalone nav/⌘K destinations. Formal nav+palette
     surfacing of these role workspaces is a pending K3 design decision
     (frontend design gate — see docs backlog "adjuster/DS dashboards"). */
  { route: '/adjuster/case/:caseId', reason: 'redirect → /adjuster/case/:caseId/analysis (AdjusterCaseIndexRedirect)' },
  { route: '/adjuster/case/:caseId/:stage', reason: 'Adjuster Case Workspace shell — stage tab' },
  { route: '/adjuster/case/:caseId/:stage/:panel', reason: 'Adjuster Case Workspace shell — stage sub-panel' },
  { route: '/data-specialist/case/:caseId', reason: 'Data-Specialist Case Workspace shell — base (renders first stage); nav/⌘K surfacing pending K3 design gate' },
  { route: '/data-specialist/case/:caseId/:stage', reason: 'Data-Specialist Case Workspace shell — stage tab' },

  /* Phase 4.4 Analysis stage — the base /analysis/:reportId is the manifest
     feature; the /* splat carries path-based tab deep-links (hash is primary). */
  { route: '/analysis/:reportId/*', reason: 'Analysis stage splat — path-based tab deep-links (base is the manifest feature)' },

  /* Router internals */
  { route: '*', reason: '404 catch-all (NotFound)' },
];

/** Routes registered as discoverable features. */
export const MANIFEST_ROUTES: ReadonlySet<string> = new Set(
  TOOLS_MANIFEST.filter((e) => e.route !== null).map((e) => e.route as string),
);

/** Routes explicitly excluded from feature nav (with a reason). */
export const EXCLUDED_ROUTE_SET: ReadonlySet<string> = new Set(
  EXCLUDED_ROUTES.map((e) => e.route),
);

/** Non-routed capabilities (§0.1) — must each declare a surface. */
export const CAPABILITY_ENTRIES: ToolManifestEntry[] = TOOLS_MANIFEST.filter(
  (e) => e.route === null,
);
